/**
 * MotionOverlayComponents - Shared components for motion overlays (DRY)
 * Used by both TimeMotionOverlay and PlanetDragOverlay
 */

import React from 'react';

// =============================================================================
// Constants
// =============================================================================

export const SHIFT_MULTIPLIER = 10;

// =============================================================================
// Navigation Button
// =============================================================================

interface NavButtonProps {
  direction: 'back' | 'forward';
  disabled: boolean;
  waiting: boolean;
  isPartial: boolean;
  onNavigate: () => void;
  onDisabledClick: () => void;
  onStop: () => void;
}

export const NavButton: React.FC<NavButtonProps> = ({
  direction,
  disabled,
  waiting,
  isPartial,
  onNavigate,
  onDisabledClick,
  onStop,
}) => {
  const handleStart = () => {
    if (!disabled) {
      onNavigate();
    } else if (isPartial) {
      onDisabledClick();
    }
  };

  return (
    <button
      className={`planet-drag-overlay__nav-btn ${disabled ? 'planet-drag-overlay__nav-btn--disabled' : ''} ${waiting ? 'planet-drag-overlay__nav-btn--waiting' : ''}`}
      onMouseDown={handleStart}
      onMouseUp={onStop}
      onMouseLeave={onStop}
      onTouchStart={handleStart}
      onTouchEnd={onStop}
      aria-label={direction === 'back' ? 'Go back' : 'Go forward'}
    >
      {direction === 'back' ? '◀' : '▶'}
    </button>
  );
};

// =============================================================================
// Buffer Position Indicator
// =============================================================================

interface BufferIndicatorProps {
  currentIndex: number;
  centerIndex: number;
  bufferSize: number; // Total buffer size (e.g., 601 for time, 721 for degrees)
  loadingProgress?: number;
}

export const BufferIndicator: React.FC<BufferIndicatorProps> = ({
  currentIndex,
  centerIndex,
  bufferSize,
  loadingProgress = 100,
}) => {
  const halfSize = Math.floor(bufferSize / 2);
  const bufferOffset = currentIndex - centerIndex;
  // Position as percentage (0% = left edge, 50% = center, 100% = right edge)
  const positionPercent = ((bufferOffset + halfSize) / bufferSize) * 100;

  return (
    <div style={{
      width: '120px',
      height: '4px',
      backgroundColor: 'rgba(180, 180, 180, 0.6)',
      borderRadius: '2px',
      position: 'relative',
      margin: '4px auto',
    }}>
      {/* Loaded portion (white, expands from center) - z-index: 1 */}
      <div style={{
        position: 'absolute',
        height: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '2px',
        zIndex: 1,
        left: `${50 - loadingProgress / 2}%`,
        width: `${loadingProgress}%`,
        transition: 'left 0.2s ease-out, width 0.2s ease-out',
      }} />
      {/* Progress from center (yellow) - z-index: 2 */}
      <div style={{
        position: 'absolute',
        height: '100%',
        backgroundColor: '#f0c040',
        borderRadius: '2px',
        zIndex: 2,
        left: bufferOffset >= 0 ? '50%' : `${positionPercent}%`,
        width: `${Math.abs(bufferOffset) / bufferSize * 100}%`,
        transition: 'left 0.1s ease-out, width 0.1s ease-out',
      }} />
      {/* Position dot - z-index: 3 (always on top) */}
      <div style={{
        position: 'absolute',
        width: '8px',
        height: '8px',
        backgroundColor: '#333',
        borderRadius: '50%',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 3,
        left: `calc(${positionPercent}% - 4px)`,
        transition: 'left 0.1s ease-out',
      }} />
    </div>
  );
};

// =============================================================================
// Action Buttons
// =============================================================================

interface ActionButtonsProps {
  hasChanged: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  hasChanged,
  onCancel,
  onConfirm,
}) => {
  return (
    <div className="planet-drag-overlay__actions">
      <button
        className="planet-drag-overlay__action-btn planet-drag-overlay__action-btn--cancel"
        onClick={onCancel}
      >
        Cancel
      </button>
      <button
        className={`planet-drag-overlay__action-btn planet-drag-overlay__action-btn--confirm ${
          !hasChanged ? 'planet-drag-overlay__action-btn--disabled' : ''
        }`}
        onClick={onConfirm}
        disabled={!hasChanged}
      >
        Apply
      </button>
    </div>
  );
};

// =============================================================================
// Hint
// =============================================================================

export const NavigationHint: React.FC = () => (
  <div className="planet-drag-overlay__hint">
    Shift = {SHIFT_MULTIPLIER}x step
  </div>
);

// =============================================================================
// Loading Overlay
// =============================================================================

interface LoadingOverlayProps {
  label: string;
  progress: number;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  label,
  progress,
}) => {
  return (
    <div className="planet-drag-overlay planet-drag-overlay--loading planet-drag-overlay--in-svg">
      <div className="planet-drag-overlay__content">
        <div className="planet-drag-overlay__spinner" />
        <span className="planet-drag-overlay__text">
          Loading {label}...
        </span>
        {progress > 0 && (
          <div className="planet-drag-overlay__progress">
            <div
              className="planet-drag-overlay__progress-bar"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Formatters
// =============================================================================

export function applyTimezoneCorrection(date: Date, correctionHours: number): Date {
  if (correctionHours === 0) return date;
  return new Date(date.getTime() + correctionHours * 60 * 60 * 1000);
}

export function formatTime(date: Date, correctionHours: number = 0): string {
  const correctedDate = applyTimezoneCorrection(date, correctionHours);
  return correctedDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function formatDate(date: Date, correctionHours: number = 0): string {
  const correctedDate = applyTimezoneCorrection(date, correctionHours);
  return correctedDate.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format offset from seconds as human-readable duration
 * Examples: "offset: 0", "offset: +10s", "offset: -5m", "offset: +2m 30s"
 */
export function formatOffsetFromSeconds(seconds: number): string {
  if (seconds === 0) return 'offset: 0';

  const totalSeconds = Math.abs(seconds);
  const sign = seconds >= 0 ? '+' : '-';

  if (totalSeconds < 60) {
    return `offset: ${sign}${totalSeconds}s`;
  }

  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (secs === 0) {
      return `offset: ${sign}${minutes}m`;
    }
    return `offset: ${sign}${minutes}m ${secs}s`;
  }

  if (totalSeconds < 86400) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (minutes === 0) {
      return `offset: ${sign}${hours}h`;
    }
    return `offset: ${sign}${hours}h ${minutes}m`;
  }

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  if (hours === 0) {
    return `offset: ${sign}${days}d`;
  }
  return `offset: ${sign}${days}d ${hours}h`;
}

/**
 * Format degree offset
 * Examples: "offset: 0°", "offset: +5.5°", "offset: -10°"
 */
export function formatDegreeOffset(degrees: number): string {
  if (degrees === 0) return 'offset: 0°';
  const sign = degrees >= 0 ? '+' : '-';
  const abs = Math.abs(degrees);
  // Format with appropriate precision
  if (abs >= 1) {
    return `offset: ${sign}${abs.toFixed(1)}°`;
  }
  if (abs >= 0.1) {
    return `offset: ${sign}${abs.toFixed(2)}°`;
  }
  return `offset: ${sign}${abs.toFixed(3)}°`;
}
